import {
  MentionTag,
  MetadataTag,
  TagContainer,
  ThemeTag,
} from '@/features/analyze/components/Tag';
import {
  BuildingOffice2Icon,
  CubeIcon,
  GlobeAltIcon,
  HashtagIcon,
  TagIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import {
  CollapsibleTagList,
  BookmarkButton,
  ExpandableContent,
  ExternalLink,
  Link,
} from '@/components/Elements';
import { TopicTagWithTooltip } from '@/features/analyze/components/TopicTagWithTooltip';
import { getSafeExternalUrl } from '@/utils/url';

import { useSearchResultTagFilter } from '@/features/analyze/hooks/useSearchResultTagFilter';
import { type FacetKey } from '@/features/analyze/types';
import { FragmentOf, graphql, readFragment } from '@/lib/graphql';

const foamTreeSelectItemFragment = graphql(`
  fragment FoamTreeSelectItemFragment on ActiveArticle {
    id
    code
    title
    url
    summary
    publishedAt
    country
    organizationType
    organization
    displayableTopicTags {
      lookupKey
      name
      description
      themes
      isSocialIssue
    }
    themes
    people
    companies
    locations
    isBookmarkedByCurrentUser
  }
`);

type FoamTreeSelectItemProps = {
  selectedArticle: FragmentOf<typeof foamTreeSelectItemFragment>;
};

export const FoamTreeSelectItem = ({
  selectedArticle,
}: FoamTreeSelectItemProps) => {
  const item = readFragment(foamTreeSelectItemFragment, selectedArticle);
  const { t } = useTranslation();
  const addSearchResultTagFilter = useSearchResultTagFilter();
  const safeArticleUrl = getSafeExternalUrl(item.url);

  if (!item) return <></>;

  const onClick = (targetFieldKey: FacetKey, clickText: string | null) => {
    if (clickText == null) return;
    addSearchResultTagFilter(targetFieldKey, clickText);
  };

  return (
    <div className="flex flex-col gap-y-3">
      <div className="flex items-start justify-between gap-x-3">
        <h4 className="min-w-0 flex-1 text-xl font-semibold">
          {safeArticleUrl ? (
            <ExternalLink to={safeArticleUrl} underline="hover">
              {item.title}
            </ExternalLink>
          ) : (
            <Link to={`/articles/${item.code}?from=treemap`}>{item.title}</Link>
          )}
        </h4>
        <div className="mt-1 shrink-0">
          <BookmarkButton
            key={item.code}
            code={item.code}
            title={item.title}
            url={item.url}
            isBookmarked={item.isBookmarkedByCurrentUser}
            className="p-0"
          />
        </div>
      </div>
      <TagContainer>
        {item.themes && item.themes.length > 0 && (
          <CollapsibleTagList
            items={item.themes}
            maxVisibleItems={3}
            textColor="text-rw-sub"
            renderTag={(theme: string) => (
              <ThemeTag
                key={theme}
                icon={HashtagIcon}
                tableName={theme}
                onClick={() => addSearchResultTagFilter('themes', theme)}
              />
            )}
          />
        )}
        {item.country && (
          <MetadataTag
            icon={GlobeAltIcon}
            text={item.country}
            onClick={() => onClick('countries', item.country)}
          />
        )}
        {item.organizationType && (
          <MetadataTag
            icon={CubeIcon}
            text={item.organizationType}
            onClick={() => onClick('organizationTypes', item.organizationType)}
          />
        )}
        {item.organization && (
          <MetadataTag
            icon={BuildingOffice2Icon}
            text={item.organization}
            onClick={() => onClick('organizations', item.organization)}
          />
        )}
        {item.displayableTopicTags && item.displayableTopicTags.length > 0 && (
          <CollapsibleTagList
            items={item.displayableTopicTags}
            maxVisibleItems={10}
            textColor="text-rw-black"
            renderTag={(tag) => (
              <TopicTagWithTooltip
                key={tag.lookupKey}
                name={tag.name}
                description={tag.description}
                themes={tag.themes}
                isSocialIssue={tag.isSocialIssue}
                icon={TagIcon}
                onClick={() => onClick('topicTags', tag.lookupKey)}
              />
            )}
          />
        )}
      </TagContainer>
      {safeArticleUrl && (
        <ExternalLink to={safeArticleUrl} className="w-fit text-sm">
          {t('原文サイトを開く')}
        </ExternalLink>
      )}
      <ExpandableContent aria-label={t('要約')} className="line-clamp-6">
        {`${new Date(item.publishedAt).toLocaleDateString()} - ${item.summary ?? ''}`}
      </ExpandableContent>

      <hr />

      <TagContainer title={t('言及された人物')}>
        {item.people?.map((person) => (
          <MentionTag key={person} text={person} />
        )) ?? '-'}
      </TagContainer>
      <TagContainer title={t('言及された組織')}>
        {item.companies?.map((company) => (
          <MentionTag key={company} text={company} />
        )) ?? '-'}
      </TagContainer>
      <TagContainer title={t('言及された地名')}>
        {item.locations?.map((location) => (
          <MentionTag key={location} text={location} />
        )) ?? '-'}
      </TagContainer>
    </div>
  );
};

FoamTreeSelectItem.fragments = {
  data: foamTreeSelectItemFragment,
};
